import { useApolloClient } from '@apollo/react-hooks';
import { IDropdownOption, TextField } from '@fluentui/react';
import { ApolloError } from 'apollo-client';
import Select from 'components/controls/Select';
import React, { useCallback, useEffect, useState } from 'react';
import { StyledControlContainer } from 'shared/styled/control.styled';
import {
   StyledContainer,
   StyledPanelColumn,
   StyledPanelGrid,
   StyledPanelRow,
   StyledPanelTitle
} from 'shared/styled/panel.stayled';
import { DayFormat, MonthFormat, renderDate, renderTime, WeekdayFormat, YearFormat } from 'shared/utils/format.utils';
import { mapToDropdownOptions } from 'shared/utils/mapping.utils';
import { Completion } from '../../../queries/view-issue.queries.types';
import { IClosureReason, IObservationCompletionLookupData } from 'shared/queries/observation-completion.queries.types';
import CompletedByDisplay from 'components/CompletedByDisplay';
import { ObservationFileEntity } from 'shared/types/file/ObservationFileEntity';
import { GetIssueCompletionLookups } from '../../../queries/issue-completion.queries';
import { NewEvidenceDocuments } from 'components/EvidenceDocuments/components/NewEvidenceDocuments/NewEvidenceDocuments';

interface IssueCompletionProps {
   observationUniqueId: string;
   issueCompletion: Completion | undefined;
   files: ObservationFileEntity[];
}

const ViewIssuesCompletion = ({ observationUniqueId, issueCompletion, files }: IssueCompletionProps) => {
   const client = useApolloClient();
   const [loadingClosureReasons, setLoadingClosureReasons] = useState<boolean>(true);
   const [loadingClosureReasonsError, setLoadingClosureReasonsError] = useState<ApolloError>();
   const [closureReasons, setClosureReasons] = useState<IClosureReason[]>();

   useEffect(() => {
      loadReporterTypes();
   }, []);

   const loadReporterTypes = async () => {
      setLoadingClosureReasons(true);

      try {
         let result = await client.query<IObservationCompletionLookupData>({
            query: GetIssueCompletionLookups,
            variables: { request: { type: 'ISSUECLOSUREREASON', selectedLanguage: 'GB' } },
            fetchPolicy: 'network-only'
         });
         if (result?.data?.closureReasons != null) {
            setClosureReasons(result.data.closureReasons);
         }
      } catch (e: any) {
         setLoadingClosureReasonsError(e);
      } finally {
         setLoadingClosureReasons(false);
      }
   };

   const renderCompletionDateTime = useCallback(() => {
      const datePart = renderDate(issueCompletion?.closeDate, {
         day: DayFormat.Numeric,
         month: MonthFormat.Short,
         weekday: WeekdayFormat.Short,
         year: YearFormat.Numeric
      });

      const timePart = renderTime(issueCompletion?.closeDate?.toLocaleString() ?? '00:00');

      return `${datePart} ${timePart}`;
   }, [issueCompletion]);

   return (
      <React.Fragment>
         <StyledContainer>
            <StyledPanelGrid fluid>
               <StyledPanelRow>
                  <StyledPanelColumn md={12} lg={6} xl={6}>
                     <StyledPanelTitle>Completion Date</StyledPanelTitle>
                     <StyledControlContainer>
                        <TextField label='' value={renderCompletionDateTime()} onChange={(e, v) => ''} disabled />
                     </StyledControlContainer>

                     <StyledPanelTitle>Completed By</StyledPanelTitle>
                     <StyledControlContainer>
                        <CompletedByDisplay completedByEmployeeId={issueCompletion?.employeeId} />
                     </StyledControlContainer>

                     <StyledPanelTitle>Closure Reason</StyledPanelTitle>
                     <StyledControlContainer>
                        <Select
                           required
                           selectedKey={issueCompletion?.closureReason}
                           multiSelect={false}
                           onChange={(e, v) => ''}
                           options={mapToDropdownOptions<IClosureReason, IDropdownOption>(
                              closureReasons,
                              reason => reason.name.toString().toUpperCase(),
                              reason => reason.description
                           )}
                           isLoading={loadingClosureReasons}
                           disabled
                        />
                     </StyledControlContainer>

                     <StyledPanelTitle>Actions Taken</StyledPanelTitle>
                     <StyledControlContainer>
                        <TextField
                           label=''
                           multiline
                           value={issueCompletion?.actionTaken}
                           onChange={(e, v) => ''}
                           disabled
                        />
                     </StyledControlContainer>
                     <NewEvidenceDocuments
                        handleAddEvidence={() => {}}
                        handleDeleteEvidence={() => {}}
                        handleUpdateFiles={() => {}}
                        isReadonly={true}
                        files={files}
                        selectedAttachmentOption={issueCompletion?.hasAttachments ? 1 : 0}
                        setSelectedAttachmentOption={() => {}}
                        observationUniqueId={observationUniqueId}
                        useEnglish
                     ></NewEvidenceDocuments>
                  </StyledPanelColumn>
               </StyledPanelRow>
            </StyledPanelGrid>
         </StyledContainer>
      </React.Fragment>
   );
};

export default ViewIssuesCompletion;